import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router } from '@angular/router';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { SessionService } from './session.service';
import { UsersApiService } from './users-api.service';
import { Organization } from './user.model';

const ALLOWED_ROLES = ['ORG_ADMIN', 'ORG_MEMBER', 'ROOT_ADMIN'];

/**
 * Guard for org-scoped routes (/org/:org_id/...).
 * Allows only ORG_ADMIN, ORG_MEMBER, and ROOT_ADMIN.
 *
 * Fast path: if the in-memory session already has the correct org, passes
 * through immediately. Otherwise, looks up the org from the API and restores
 * the session — this enables shared links and page refreshes to work without
 * going through the login flow again.
 */
export const orgContextGuard: CanActivateChildFn = (childRoute: ActivatedRouteSnapshot) => {
    // Walk the full route path from root to find the :org_id segment, regardless
    // of how deep in the route tree this guard fires.
    const orgId = childRoute.pathFromRoot
        .map(r => r.params['org_id'])
        .find(Boolean) as string | undefined;

    const router = inject(Router);

    if (!orgId) return router.createUrlTree(['/org-not-found']);

    const sessionService = inject(SessionService);
    const usersApi       = inject(UsersApiService);

    return sessionService.assumedRole$.pipe(
        take(1),
        switchMap(role => {
            if (!ALLOWED_ROLES.includes(role)) return of(router.createUrlTree(['/']));

            // Fast path: session already has the correct org.
            if (sessionService.accountOrg$.getValue().organization?.id === orgId) {
                return of(true);
            }

            // Restore org context from the URL (shared link / page refresh).
            return usersApi.getOrganizations$().pipe(
                take(1),
                switchMap(orgs => {
                    const org = orgs.find(o => o.org_id === orgId);
                    if (org) {
                        return sessionService
                            .setAccountOrg({ id: org.account_id, name: org.account_name }, org)
                            .pipe(map(() => true));
                    }
                    // Not in membership list (e.g. ROOT_ADMIN accessing any org).
                    // Fetch the org directly so we can sync session with the URL org.
                    return usersApi.getOrganizationById$(orgId as string).pipe(
                        take(1),
                        switchMap((fetchedOrg: Organization) =>
                            sessionService
                                .setAccountOrg({ id: fetchedOrg.account_id, name: fetchedOrg.account_name }, fetchedOrg)
                                .pipe(map(() => true))
                        ),
                        catchError(() => of(true)), // org not accessible — let through, API will 403
                    );
                }),
                catchError(() => of(true)),
            );
        }),
    );
};
